import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const VideoStar = ({ starType, size = 85 }) => {
    
    const [isVisible, setIsVisible] = useState(false);
    const [useWebM, setUseWebM] = useState(true);
    const [isSafari, setIsSafari] = useState(false);
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasStartedLoading, setHasStartedLoading] = useState(false);
    
    // Determine if we should use the large star videos
    const useLargeStars = size > 100;

    // Check for Safari and WebM support
    useEffect(() => {
        //console.log('VideoStar: Checking browser support');
        const checkBrowserSupport = () => {
            const ua = window.navigator.userAgent.toLowerCase();
            const isSafariCheck = (
                (ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1) || // Desktop Safari
                /iphone|ipod|ipad/.test(ua) // iOS Safari and all iOS WebViews
            );
            setIsSafari(isSafariCheck);

            // Only check WebM support if not Safari
            if (!isSafariCheck) {
                const video = document.createElement('video');
                const canPlayWebM = video.canPlayType('video/webm; codecs="vp8,vp9"');
                setUseWebM(canPlayWebM === 'probably' || canPlayWebM === 'maybe');
            } else {
                setUseWebM(false);
            }
        };

        checkBrowserSupport();
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = async () => {
            try {
                if (isVisible) {
                    await video.play().catch(() => {
                        // Ignore play interruption errors
                    });
                }
            } catch (error) {
                console.error('Video playback error:', error);
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    setIsVisible(entry.isIntersecting);
                });
            },
            { threshold: 0.1 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        if (isVisible) {
            handlePlay();
        } else {
            video.pause();
        }

        return () => {
            observer.disconnect();
            video.pause();
        };
    }, [isVisible]);

    // Handle video loading
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const videoPath = getVideoPath();

        const handleLoadStart = () => {
            setHasStartedLoading(true);
        };

        const handleLoadedData = () => {
            setIsLoading(false);
        };

        const handleError = (error) => {
            console.error('VideoStar: Loading error:', error);
            setIsLoading(false);
        };

        const handleCanPlay = () => {
            setIsLoading(false);
        };

        video.addEventListener('loadstart', handleLoadStart);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('error', handleError);
        video.addEventListener('canplay', handleCanPlay);

        // Set the source and start loading
        video.src = videoPath;
        video.load();

        return () => {
            video.removeEventListener('loadstart', handleLoadStart);
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('error', handleError);
            video.removeEventListener('canplay', handleCanPlay);
        };
    }, [starType, isSafari, useWebM]);

    // Determine the video path based on size and format
    const getVideoPath = () => {
        const starNumber = starType || Math.floor(Math.random() * 5) + 1;
        const basePath = useLargeStars ? 'stars' : 'stars/sml';
        const prefix = useLargeStars ? 'Star' : 'Small_star';
        
        let path;
        if (isSafari) {
            path = `/assets/${basePath}/fallback/${prefix}_${starNumber}.mov`;
        } else {
            const format = useWebM ? 'webm' : 'fallback';
            const extension = useWebM ? 'webm' : 'mp4';
            path = `/assets/${basePath}/${format}/${prefix}_${starNumber}.${extension}`;
        }
        
        return path;
    };

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <div ref={containerRef} className="w-full h-full flex items-center justify-center" style={{ isolation: 'isolate' }}>
                <div 
                    className="relative flex items-center justify-center bg-transparent"
                    style={{ 
                        width: `${size}px`, 
                        height: `${size}px`,
                        isolation: 'isolate'
                    }}
                >
                    {isLoading && (
                        // Loading state with pulsing asterisk
                        <div className="absolute inset-0 flex items-center justify-center">
                            <motion.div
                                className="text-still-white text-glow-still text-2xl"
                                animate={{
                                    opacity: [0.5, 1, 0.5],
                                }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                *
                            </motion.div>
                        </div>
                    )}
                    <video
                        ref={videoRef}
                        autoPlay={isVisible}
                        loop
                        muted
                        playsInline
                        disablePictureInPicture
                        preload="auto"
                        className={`w-full h-full object-cover relative z-10 mix-blend-screen ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                        style={{
                            mixBlendMode: 'screen',
                            background: 'transparent',
                            transition: 'opacity 0.5s ease-out'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default VideoStar; 